import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import * as api from '@/api/v4/linkedin'
import {
  JobDescription,
  LinkedinReport,
  LioCheck,
  LioEducation,
  LioFinding,
  LioPosition,
} from '@/types'

export const useLinkedinScanStore = defineStore('linkedinScanStore', () => {
  const isLoadingLinkedinScan = ref<boolean>(false)
  const linkedinScan = ref<LinkedinReport | null>(null)
  const selectedNavLink = ref<string>('Summary')
  const score = ref<number>(0)
  const improvementAmount = ref<number>(0)
  const checkedAmount = ref<number>(0)
  const linkedinProfileUrl = ref<string>('')
  const jobDescriptionsId = ref<number[]>([])
  const jobDescriptions = ref<JobDescription[]>([])
  const reportCreatedAt = ref<string>('')
  const headline = ref<string>('')
  const summary = ref<string>('')

  function setSelectedNavLink(link: string) {
    selectedNavLink.value = link
  }

  const getBasicInfoData = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'Full name',
        nameTooltip:
          'Recruiters and hiring managers need to be sure that the profile they’ve found on LinkedIn corresponds to the resume they have in hand. Be sure your first and last name are present and identical to the documents you’ve submitted in your application.',
        ...getValues('profileFullName'),
      },
      {
        name: 'Profile picture',
        nameTooltip:
          'It helps humanize your job application when recruiters and hiring managers can put a face to the name. Having a professional, clear LinkedIn profile picture is highly beneficial to your job search. Get noticed!',
        ...getValues('profilePicture'),
      },
      {
        name: 'Background picture',
        nameTooltip:
          'A background picture is a great way to show off your personality and make your LinkedIn profile stand out. It’s a great way to show off your interests and hobbies, and can be a great conversation starter. Make sure your background picture is professional and relevant to your industry.',
        ...getValues('backgroundPicture'),
      },
      {
        name: 'Location',
        nameTooltip:
          'It’s helpful for recruiters and hiring managers to see that you’re in the vicinity of the job they’re looking to fill. If you’re not in the area, be sure to list the places to which you’d be willing to relocate in your Profile Summary below and Career Interests preferences in LinkedIn Jobs . The more details recruiters and hiring managers can see at first glance, the better your chances of being contacted through LinkedIn.',
        ...getValues('profileLocation'),
      },
      {
        name: 'Industry',
        nameTooltip:
          'It’s important in a standard LinkedIn search to have your job industry present on your profile. Selecting an industry allows a broader search to find you, and gives a more narrow search a better idea of what it is you’re looking for. The more details you can give in your LinkedIn, the more likely the correct type of recruiter or hiring manager will find you.',
        ...getValues('profileIndustry'),
      },
      {
        name: 'Open To Work',
        nameTooltip:
          "The 'Open to Work' feature on LinkedIn increases your profile's visibility to recruiters, hiring managers, and the LinkedIn community who might recommend you. You can set it to be visible to recruiters only for discreet job searching, or to everyone, which can lead to more profile views and potential opportunities. Enabling this feature can significantly enhance your chances of being contacted for job opportunities.",
        ...getValues('openToWork'),
      },
    ]
  })

  const getWorkExperienceChecks = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'History',
        nameTooltip: `Make sure your work history is accurate and that it matches your resume.
          <br />
          <br />
          We recommend having at least 3 experiences listed so that you have the best chance of adding your awesome accomplishments, measurable results, and keywords.`,
        ...getValues('workHistoryLength'),
      },
      {
        name: 'Short term gigs',
        nameTooltip: `Recruiters view short-term employment as a potential red flag.
          <br />
          <br />
          If applicable, make sure any short-term or temp gigs are labeled as “Contract,” “Intern,” “Part-time,” or "Consulting." If not, consider removing short-term jobs from your work experience.`,
        ...verifyShortTermGigs(),
      },
      {
        name: 'Job title match',
        nameTooltip: `Your job title within this role is important to include from a recruiter or hiring manager\’s perspective. It\’s vital to show where you\’ve been so that a quick look at your profile will show your career progression.
          <br />
          <br />
          It\’s important to have these job titles present as part of your work experience so that you\’re more likely to show up in a LinkedIn search that contains this job title.`,
        ...getValues('workJobTitle'),
      },
      {
        name: 'Measurable results',
        nameTooltip: `Measurable results can set a candidate apart from the rest, especially in a broad LinkedIn search. When you have concrete, quantifiable examples of your success the proof of your value is undeniable.
          <br />
          <br />
          These measurable results are also a fast way to get recruiters and hiring managers attention in a fast-paced candidate search.`,
        ...getValues('workMeasurableResults'),
        ...getEvidence('workMeasurableResults'),
        itemTooltipTitle: 'Adding Measurable Results',
        itemTooltip: `
        Recruiters scanning resumes typically notice and hone in on definitive results, shown in numbers. The more specific you can be (while remaining concise), the better- add dates, length of time, and accurate numbers whenever possible.
        <h1>Examples:</h1>
        <ul>
          <li>Supervised up to 25 employees</li>
          <li>Reduced labor costs by more than 15%</li>
          <li>Increased sales from $1.2 to $2.5 million in 2017</li>
          <li>Ranked in top 5% of employee performance for 3 years running</li>
        </ul>
        <h1>Measurable result categories:</h1>
        <h2>Money</h2>
        <ul>
          <li>sales volume</li>
          <li>number of units sold</li>
          <li>dollars and percentages with revenue</li>
          <li>share holder value increase</li>
          <li>cost savings</li>
          <li>budgets or funds served</li>
        </ul>
        <h2>People</h2>
        <ul>
          <li>numbers of customers served</li>
          <li>number of customers retained</li>
          <li>number of new customers</li>
          <li>numbers of direct reports</li>
          <li>number of people managed or hired</li>
          <li>size of teams led</li>
        </ul>
        <h2>Tasks</h2>
        <ul>
          <li>numbers of any tasks done in great quantity</li>
          <li>efficiency increases</li>
          <li>process improvements</li>
          <li>time saved</li>
        </ul>
        <h2>Other</h2>
        <ul>
          <li>numbers of awards or publications</li>
        </ul>
        `
      },
      {
        name: 'Words to avoid',
        nameTooltip: `Hiring managers and recruiters prefer action words that define specific experiences, skills and accomplishments rather than cliched phrases, buzzwords or over-aggressive tones.`,
        ...verifyWordsToAvoid(),
        itemTooltipTitle: 'Negative words, phrases and sentiment',
        itemTooltip: `
        Hiring managers and recruiters prefer action words that define specific experiences, skills and accomplishments rather than cliched phrases, buzzwords or over-aggressive tones.
        A few examples of negative words or phrases:
        <br />
        <br />
        <ul>
          <li><b>Best of Breed</b> - Avoid cliches that does not bring value, leave the dog at home</li>
          <li><b>Synergy</b> - Avoid phony business jargon. Use more concrete language to convey your point more effectively.</li>
          <li><b>Results driven</b> - Avoid vague self-appointed adjectives. Instead, show you're results driven (or otherwise) with actual impressive results you achieved.</li>
          <li><b>Aggressive close of deals</b> - Avoid phrases that are overly negative in tone</li>
        </ul>
        `
      },
    ]
  })

  const getHighImpactHeadlineData = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'Headline',
        nameTooltip: `The headline is your basic introduction. At a glance it should be clear that your headline is up to date, informative, and representative of you as a candidate.
        <br />
        <br />
        Having your job title in your headline is important to the LinkedIn search feature. When you include it in your headline, you\’re more likely to be found within a search looking to fill that type of position. Be sure to include this within your LinkedIn profile\’s headline.
        <br />
        <br />
        Your headline should be as descriptive as possible. If you\’re an engineer who works solely in mobile technology, for example, your headline should represent that. Having the little details gives a better full-picture view of you as a candidate at first look. It also aids in your relevancy in a candidate search that includes those keywords.
        <br />
        <br />
        Good LinkedIn headlines are 6-12 words and take advantage of the 120 character limit.`,
        isUserData: true,
        value: `"${linkedinScan.value.headline}"`,
      },
      {
        ...getValues('headlineLength'),
      },
      {
        ...getValues('headlineJobTitle'),
      },
      {
        ...getValues('headlineSkills'),
      },
      {
        ...getValues('headlineSpecialCharacters'),
      },
    ]
  })

  const getHighImpactSummaryData = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'Profile summary',
        nameTooltip: `Your profile summary should be your elevator pitch. Using specific keywords and providing examples of your best work will give a detailed and positive first impression when you show up in candidate searches.
          <br />
          <br />
          Your profile summary should include the keywords that are most relevant to your experience, including your top hard skills. LinkedIn’s algorithms are sensitive enough to pick up on keywords within this section, so it is important to put your best, most optimized foot forward.
          <br />
          <br />
          Good LinkedIn profile summaries are 250-300 words and take advantage of the generous 2000 character limit.`,
        isUserData: true,
        value: `"${linkedinScan.value.summary}"`,
      },
      {
        ...getValues('summaryLength'),
      },
      {
        ...getValues('summaryJobTitle'),
      },
      {
        ...getValues('summarySkills'),
      },
      {
        ...getValues('summarySpecialCharacters'),
      },
      {
        ...getValues('summaryTone'),
        ...getEvidence('summaryTone'),
        itemTooltipTitle: 'Negative words, phrases and sentiment',
        itemTooltip: `
        Hiring managers and recruiters prefer action words that define specific experiences, skills and accomplishments rather than cliched phrases, buzzwords or over-aggressive tones.
        A few examples of negative words or phrases:
        <br />
        <br />
        <ul>
          <li><b>Best of Breed</b> - Avoid cliches that does not bring value, leave the dog at home</li>
          <li><b>Synergy</b> - Avoid phony business jargon. Use more concrete language to convey your point more effectively.</li>
          <li><b>Results driven</b> - Avoid vague self-appointed adjectives. Instead, show you're results driven (or otherwise) with actual impressive results you achieved.</li>
          <li><b>Aggressive close of deals</b> - Avoid phrases that are overly negative in tone</li>
        </ul>
        `
      },
      {
        ...getValues('summaryMeasurableResults'),
        ...getEvidence('summaryMeasurableResults'),
        itemTooltipTitle: 'Adding Measurable Results',
        itemTooltip: `
        Recruiters scanning resumes typically notice and hone in on definitive results, shown in numbers. The more specific you can be (while remaining concise), the better- add dates, length of time, and accurate numbers whenever possible.
        <h1>Examples:</h1>
        <ul>
          <li>Supervised up to 25 employees</li>
          <li>Reduced labor costs by more than 15%</li>
          <li>Increased sales from $1.2 to $2.5 million in 2017</li>
          <li>Ranked in top 5% of employee performance for 3 years running</li>
        </ul>
        <h1>Measurable result categories:</h1>
        <h2>Money</h2>
        <ul>
          <li>sales volume</li>
          <li>number of units sold</li>
          <li>dollars and percentages with revenue</li>
          <li>share holder value increase</li>
          <li>cost savings</li>
          <li>budgets or funds served</li>
        </ul>
        <h2>People</h2>
        <ul>
          <li>numbers of customers served</li>
          <li>number of customers retained</li>
          <li>number of new customers</li>
          <li>numbers of direct reports</li>
          <li>number of people managed or hired</li>
          <li>size of teams led</li>
        </ul>
        <h2>Tasks</h2>
        <ul>
          <li>numbers of any tasks done in great quantity</li>
          <li>efficiency increases</li>
          <li>process improvements</li>
          <li>time saved</li>
        </ul>
        <h2>Other</h2>
        <ul>
          <li>numbers of awards or publications</li>
        </ul>
        `
      },
    ]
  })

  const getEducationChecks = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'Education',
        nameTooltip: `Many recruiters include a degree requirement within their LinkedIn searches, so providing these details is key.
          <br />
          <br />
          Education also provides connection opportunities based on university affiliation plus the opportunity to list trade or educational courses you took to advance your skills.`,
        ...getValues('educationIsProvided'),
      },
      {
        name: 'Degree match',
        nameTooltip: `If a job description explicitly calls for a degree preference, such as Bachelor's of Arts or MBA, it's highly likely that recruiters scouring LinkedIn for candidates will also add this to their search queries.
          <br />
          <br />
          Be sure to add any degrees you've completed and that the degree is explicitly stated.`,
        ...getValues('educationExperienceMatchJobDescriptions'),
      },
    ]
  })

  const getPredictedSkillsCheck = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'Skills count',
        nameTooltip: `Jobscan detects the average number and types of of skills across industries. In general, the number of skills listed on your LinkedIn profile should be on par with the industry average.
        <br />
        <br />
        We use machine learning to predict some common and key skills we think you already have but is not listed on your LinkedIn profile.`,
        ...getValues('skillsCount'),
      },
    ]
  })

  const getTipsAndTricksData = computed<LioCheck[]>(() => {
    if (!linkedinScan.value?.findings) return []
    return [
      {
        name: 'Vanity URL',
        nameTooltip: `Custom URLs are a great way to ensure your LinkedIn profile is easily accessible and looks good when you list it on your resume.
          <br />
          <br />
          This feature LinkedIn provides also help your first and last name to show up higher within a LinkedIn or Google search.`,
        ...getValues('profileId'),
      },
      {
        name: '# of connections',
        nameTooltip: `Increasing your number of connections verifies your previous experience by allowing more people to view, endorse and refer your LinkedIn profile.
          <br />
          <br />
          It also gives recruiters and hiring managers an idea of your level of professionalism and proactive levels online. Plus, you likely increase your search rank in LinkedIn by more direct connections to professionals.`,
        ...getValues('profileConnectionCount'),
      },
    ]
  })

  const getEducationTable = computed(() => {
    if (!linkedinScan.value?.education) return []
    return Object.values(linkedinScan.value.education).map(
      (education: LioEducation) => ({
        institution: education.schoolName,
        ...getInstitutionData(education),
      })
    )
  })

  const getKeySkillsTable = computed(() => {
    if (!linkedinScan.value?.findings) return []
    const skills = linkedinScan.value.findings.find(
      (f: LioFinding) => f.key === 'keySkillsMatch'
    )

    if (!skills?.evidence?.items) return []
    return (
      Object.values(skills.evidence.items).map((skill: any) => ({
        skillName: skill.name,
        jobDescriptionCount: skill.jdCount,
        linkedinProfileCount:
          skill.exactMatches.length + skill.aliasMatches.length,
      })) || []
    )
  })

  const getWorkExperienceTable = computed(() => {
    if (!linkedinScan.value?.positions) return []
    return Object.values(linkedinScan.value.positions).map(
      (position: LioPosition) => ({
        workExperience: position.company,
        ...getWorkExperienceData(position),
      })
    )
  })

  const getPredictedSkillsTable = computed(() => {
    if (!linkedinScan.value?.findings) return []
    const predictedSkills = linkedinScan.value.findings.find(
      (f: LioFinding) => f.key === 'recommendedSkills'
    )
    return predictedSkills?.evidence?.items || []
  })

  function getInstitutionData(institution: LioEducation) {
    return {
      dateCheck:
        institution.findings.find(
          (f: LioFinding) => f.key === 'educationExperienceDates'
        )?.status === 'pass',
      schoolCheck:
        institution.findings.find(
          (f: LioFinding) => f.key === 'educationExperienceDegree'
        )?.status === 'pass',
      degreeCheck:
        institution.findings.find(
          (f: LioFinding) => f.key === 'educationExperienceSchoolInfo'
        )?.status === 'pass',
    }
  }

  function getWorkExperienceData(position: LioPosition) {
    return {
      date:
        position.findings.find(
          (f: LioFinding) => f.key === 'workExperienceDates'
        )?.status === 'pass',
      jobTitle:
        position.findings.find(
          (f: LioFinding) => f.key === 'workExperienceJobTitle'
        )?.status === 'pass',
      companyName:
        position.findings.find(
          (f: LioFinding) => f.key === 'workExperienceCompany'
        )?.status === 'pass',
      responsibilities:
        position.findings.find(
          (f: LioFinding) => f.key === 'workExperienceDescription'
        )?.status === 'pass',
    }
  }

  function getValues(key: string) {
    const finding = linkedinScan.value?.findings.find(
      (f: LioFinding) => f.key === key
    )
    return {
      successful: ['pass', 'skip'].includes(finding?.status || ''),
      value: finding?.statusReason || 'test',
    }
  }

  function getEvidence(key: string) {
    const finding = linkedinScan.value?.findings.find(
      (f: LioFinding) => f.key === key
    )
    return {
      hasEvidence: true,
      evidenceTitle: finding?.evidence?.title,
      evidences: finding?.evidence?.items?.map(
        item => `"..${item?.token ? item.token : item}.."`
      ),
    }
  }

  function verifyShortTermGigs() {
    if (!linkedinScan.value?.positions) return { successful: false, value: '' }
    let successMessage = ''
    let failMessage = ''
    linkedinScan.value.positions.forEach((position: LioPosition) => {
      const finding = position.findings.find(
        (f: LioFinding) => f.key === 'workShortTermGigs'
      )
      if (finding?.status === 'pass') {
        successMessage = finding.statusReason
      } else {
        failMessage = finding?.statusReason || 'Please, run the report again.'
      }
    })
    return {
      successful: !failMessage,
      value: failMessage || successMessage,
    }
  }

  function verifyWordsToAvoid() {
    if (!linkedinScan.value?.findings) return { successful: false, value: '' }
    let successMessage = 'test'
    let failMessage = ''
    linkedinScan.value.positions.forEach((position: LioPosition) => {
      const finding = position.findings.find(
        (f: LioFinding) => f.key === 'workExperienceTone'
      )
      if (finding?.status === 'pass') {
        successMessage = finding.statusReason
      } else {
        failMessage = finding?.statusReason || 'Please, run the report again.'
      }
    })
    return {
      successful: !failMessage,
      value: failMessage || successMessage,
    }
  }

  async function fetchLinkedinReport() {
    if (!jobDescriptionsId.value.length || !linkedinProfileUrl.value) return
    const payload = {
      jobs: jobDescriptionsId.value,
      linkedin_profile_url: linkedinProfileUrl.value,
    }
    const { data, error } = await api.getLinkedinReport(payload)
    if (error) {
      return console.error(error)
    }
    linkedinScan.value = data
    score.value = data.score
    headline.value = data.headline
    // TODO: Use report.createdAt once the report history has been implemented.
    reportCreatedAt.value = new Date().toISOString()
    summary.value = data.summary
    improvementAmount.value = data.checks.num_warns + data.checks.num_fails
    checkedAmount.value = data.checks.num_passes
  }

  function restartScan() {
    linkedinScan.value = null
    score.value = 0
    improvementAmount.value = 0
    checkedAmount.value = 0
    headline.value = ''
    summary.value = ''
  }

  return {
    linkedinScan,
    getBasicInfoData,
    getHighImpactHeadlineData,
    getHighImpactSummaryData,
    getTipsAndTricksData,
    getWorkExperienceChecks,
    getEducationChecks,
    getPredictedSkillsCheck,
    getKeySkillsTable,
    getEducationTable,
    getWorkExperienceTable,
    getPredictedSkillsTable,
    fetchLinkedinReport,
    selectedNavLink,
    setSelectedNavLink,
    score,
    reportCreatedAt,
    improvementAmount,
    checkedAmount,
    jobDescriptions,
    jobDescriptionsId,
    isLoadingLinkedinScan,
    linkedinProfileUrl,
    summary,
    headline,
    restartScan,
  }
})
